import { HttpClient } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { map, Observable } from "rxjs";
import { API_BASE_URL } from "src/app/injection-constants";
import { SectionConfig } from "../../shared/models/section-config.model";
import { PagedResponse } from "../models/paged-response";
import { SectionConfigModel } from "../models/section-configuration.model";
import { UpdateSectionConfigResponse } from "../models/update-section-config.response";


@Injectable({
    providedIn: 'root',
})
export class SectionConfigurationService {
    constructor(private readonly http: HttpClient,
        @Inject(API_BASE_URL) private readonly apiBaseUrl: string) { }

    getSectionConfiguration(): Observable<SectionConfigModel[]> {
        return this.http.get<PagedResponse<SectionConfig>>(`${this.apiBaseUrl}/config/section`).pipe(
            map(response => {
                return response.items.sort((config1, config2) => (config1.weight ?? 0) - (config2.weight ?? 0))
                    .map(sectionConfig => this.mapSectionConfigToModel(sectionConfig))
            }))
    }

    updateSectionConfiguration(sectionConfig: SectionConfigModel): Observable<SectionConfigModel> {
        const useDateOnly = (fullDate: Date | string | null | undefined): string | null => {
            if (!fullDate) return null;

            let date: Date;
            if (typeof fullDate === 'string') {
                date = new Date(fullDate);
            } else {
                date = fullDate;
            }

            if (isNaN(date.getTime())) return null; // invalid date

            return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        };

        const formData = new FormData();
        sectionConfig.bannersConfig?.items?.forEach((item) => {
            if (item.file) {
                formData.append('bannerFiles', item.file, item.fileName);
            }

            item.startDate = useDateOnly(item.startDate);
            item.endDate = useDateOnly(item.endDate);
        });

        const data = this.mapSectionConfigModelToEntity(sectionConfig);
        const request = JSON.stringify(data);

        formData.append('sectionConfig', request);

        return this.http.put<UpdateSectionConfigResponse>(`${this.apiBaseUrl}/config/section`, formData).pipe(
            map(response => this.mapSectionConfigToModel(response.sectionConfig))
        )
    }

    deleteBanners(sectionConfigId: string, bannerUrls: string[]): Observable<void> {
        const filenames = bannerUrls.map(url => url.split('/').pop());
        return this.http.delete<void>(`${this.apiBaseUrl}/config/section/${sectionConfigId}/banners?names=${filenames.join('&names=')}`);
    }

    private mapSectionConfigToModel(sectionConfig: SectionConfig): SectionConfigModel {
        const filter = sectionConfig.filter?.filter;
        return {
            ...sectionConfig,
            setBrandColor: !!sectionConfig.brandColor,
            setBackgroundColor: !!sectionConfig.backgroundColor,
            isEmpty: !!sectionConfig.isEmpty,
            filter: {
                ...filter,
                rules: filter?.rules ?? [],
                appIds: filter?.appIds ?? []
            },
            bannersConfig: {
                ...sectionConfig.bannersConfig,
                setDotsColor: !!sectionConfig.bannersConfig?.color,
                items: !!sectionConfig.bannersConfig?.items
                    ? sectionConfig.bannersConfig?.items?.map(item => ({
                        ...item
                    }))
                    : []
            }
        }
    }

    private mapSectionConfigModelToEntity(model: SectionConfigModel): SectionConfig {
        return {
            ...model,
            filter: {
                filter: {
                    ...model.filter,
                    country: '',
                    searchTerm: '',
                    includeHidden: false
                },
                pageSize: 21
            }
        }
    }
}
